import path from 'path';
import { readFile } from 'fs/promises';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import AWS from 'aws-sdk';
import Table from 'cli-table';
// import logUpdate from 'log-update';
import descriptions from '../../helpers/descriptions.js';
import { getFolderFiles } from '../../helpers/utils.js';
import { cl, pr, printJSON, themed } from '../../helpers/print.js';
import { genCredentialsOnClientApi, resetAvailableEntities, getSupportedFlows } from '../../helpers/api.js';
import { validateAvailableConnectors, validateAvailableSources, validateAvailableTargets } from './validators.js';

const debug = Debug('commands:env:deploy');

const command = 'deploy';

const desc = 'Deploy Environment settings';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('sourceFolder', descriptions.options['sourceFolder'].config)
    .demandOption('sourceFolder', descriptions.options['sourceFolder'].demandText);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, sourceFolder } = argv;
  const { clientApiBaseUri } = hg;

  // eslint-disable-next-line no-undef
  const folderPrefix = path.resolve(process.cwd(), sourceFolder);
  let message;
  let spinner = ora();
  try {
    // 1. build list of deployable files
    message = themed(`Scanning ${themed(folderPrefix, 'info')} for deployable files`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const deployableFiles = await getFolderFiles(folderPrefix, {
      filter: {
        pattern:
          '((**/requirements.txt)|(**/availableSources.json)|(**/availableTargets.json)|(**/availableConnectors.json)|(**/customTaps.json))',
      },
    });
    if (deployableFiles.length === 0) {
      if (json) {
        printJSON({ status: 'error', error: 'There are no files to deploy at the specified location!' });
      } else {
        spinner.fail(themed(`Error: ${message}.`, 'secondary'));
        pr(themed(`Message: ${themed('There are no files to deploy at the specified location!')}`, 'secondary'));
      }
      return;
    }
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    for (const filePath of deployableFiles) {
      if (!filePath.endsWith('.json')) continue;

      try {
        const fileContent = await readFile(filePath, { encoding:'utf-8' });
        JSON.parse(fileContent);
      } catch (error) {
        throw new Error(`File ${filePath} is not a valid JSON: ${String(error)}`);
      }
    }

    // Validate files
    const flows = await getSupportedFlows({
      debug,
      baseUri: clientApiBaseUri,
      env,
      apikey,
    });
    for (const filePath of deployableFiles) {
      if (filePath.endsWith('availableSources.json')) {
        const forkedSources = JSON.parse(await readFile(filePath, { encoding:'utf-8' }));
        await validateAvailableSources(forkedSources, clientApiBaseUri, apikey, flows, env);
      }
      if (filePath.endsWith('availableTargets.json')) {
        const forkedTargets = JSON.parse(await readFile(filePath, { encoding:'utf-8' }));
        await validateAvailableTargets(forkedTargets, clientApiBaseUri, apikey, flows, env);
      }
      if (filePath.endsWith('availableConnectors.json')) {
        const forkedConnectors = JSON.parse(await readFile(filePath, { encoding:'utf-8' })); 
        await validateAvailableConnectors(forkedConnectors, clientApiBaseUri, apikey, flows, env);
      }
    }

    // 2. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'env-deploy',
      env,
      apikey,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // create authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    // 3. upload new files
    message = themed(`Deploying environment files`);
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    const rootKey = `config/`;
    const table = new Table({ head: ['File', 'Status'] });
    for await (const file of deployableFiles) {
      const filename = path.basename(file);

      message = themed(`Pushing file: ${themed(filename, 'info')}`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      const readBuffer = await readFile(file);
      const params = {
        Bucket: env,
        Key: `${rootKey}${filename}`,
        Body: readBuffer,
      };
      const res = await s3.putObject(params).promise();
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
      debug('s3-put-res', res);
      table.push([filename, 'Deployed']);
      // handling redis change
      await resetAvailableEntities({
        debug,
        baseUri: clientApiBaseUri,
        env,
        apiKey: apikey
      });
    }

    if (json) {
      printJSON({ status: 'success', deployedFiles: deployableFiles });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    !json && spinner.fail(themed(`Error: ${message}.`, 'secondary'));
    pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
    debug(err);
    if (err && err.response && err.response.data) {
      debug('response', err.response.data);
    }
  }
};

export { command, desc, builder, handler };
